import { useEffect } from 'react';

const prefersReduced = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Révèle les éléments [data-reveal] dès qu'ils atteignent le viewport.
 *
 * Balayage au défilement plutôt qu'IntersectionObserver : l'observateur ne
 * notifie que les franchissements de seuil, donc un élément « sauté »
 * (ancre, onglet, rechargement en cours de page) restait masqué à vie.
 * Ici un élément déjà dépassé est simplement considéré comme révélé.
 *
 * Re-scanne à chaque changement de `deps` (route, onglet…) pour prendre en
 * compte les éléments montés après coup.
 */
export function useReveal(deps = []) {
  useEffect(() => {
    let pending = Array.from(document.querySelectorAll('[data-reveal]')).filter(
      (n) => !n.classList.contains('is-visible')
    );

    if (prefersReduced()) {
      pending.forEach((n) => n.classList.add('is-visible'));
      return undefined;
    }

    let frame = 0;

    const sweep = () => {
      frame = 0;
      const limit = window.innerHeight * 0.92;
      pending = pending.filter((node) => {
        if (node.getBoundingClientRect().top >= limit) return true;
        node.classList.add('is-visible');
        return false;
      });
      if (!pending.length) stop();
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(sweep);
    };

    function stop() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    sweep();

    return stop;
  }, deps);
}

/** Valeur de défilement normalisée (0 → 1) sur toute la page. */
export function useScrollProgress(onChange) {
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      onChange(max > 0 ? window.scrollY / max : 0);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [onChange]);
}
